import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../../../core/i18n'
import { IJsonResponse } from '../../../../core/JsonApiSerializer'
import { agentService, utilService } from '../../../../core/services'
import * as interfaces from '../../../../static/interfaces'
import * as Hooks from '../../../../utils/hooks'
import { Button, Pagination, Buttons, StatusBadge } from '../../../atoms'
import { Panel } from '../../../atoms'
import SidebarLayout from '../../_layouts/sidebarLayout'
import Typography from 'components/atoms/_layouts/Typography'
import { COLORS } from 'static/constants'

declare var window: interfaces.IWindow

interface IProps {
  templates: {
    connection_mail_templates: IJsonResponse
  }
  user: any
}

const getTargetUserType = (record): string => {
  if (record.blank_item === 'nothing') {
    return `${I18n.t('generic.guest')} & ${I18n.t('generic.guide')}`
  }
  return record.target_user_type
}

const getBadgeType = ({ target_user_type, blank_item }) => {
  if (blank_item === 'nothing') {
    return 'usually'
  }

  switch (target_user_type) {
    case 'guest':
      return 'success'
    case 'guide':
      return 'info'
    default:
      return 'empty'
  }
}
const AgentConnectionMailTemplatesIndex: React.FC<IProps> = props => {
  Hooks.SetDocumentTitle(I18n.t('connection_mail_template', { scope: 'activerecord.models' }))
  const { data: templatesList, pagination } = agentService.getDataFromJson(
    props.templates.connection_mail_templates
  )
  const { data: userData } = utilService.getDataFromJson(props.user)

  const formattedTemplatesList = templatesList.reduce((acc, template) => {
    if (template.template_pair_id) {
      const found = acc.find(([t]) => t.template_pair_id === template.template_pair_id)
      if (found) {
        found.push(template)
        return acc
      }
    }
    return [...acc, [template]]
  }, [])

  const handleDelete = async id => {
    try {
      const { flush } = await agentService.deleteConnectionMailTemplate(id)
      if (flush) {
        window.flashMessages.addMessage({
          text: flush.message,
          type: flush.type,
        })

        if (flush.type === 'success') {
          window.location.reload()
        }
      }
    } catch (error) {
      console.error('Failed to delete connection mail template:', error)
    }
  }

  const handleClickDelete = record => {
    if (record.template_pair_id !== null) {
      const pairedTemplate = templatesList.find(template => {
        return template.template_pair_id === record.template_pair_id && template.id !== record.id
      })
      if (!pairedTemplate) {
        window.flashMessages.addMessage({
          text: 'ペアのテンプレートが見つかりませんでした。',
          type: 'warning',
        })
      }
      if (
        pairedTemplate &&
        window.confirm(
          `「${pairedTemplate.title}」とペアになっています。\n両方とも削除されますがよろしいですか？`
        )
      ) {
        handleDelete(record.id)
      }
      return
    }

    if (window.confirm('削除してよろしいですか？')) {
      handleDelete(record.id)
    }
  }

  const renderRecordControls = record => (
    <Buttons inline={true}>
      <Button key="edit" small={true}>
        <a href={`/agent/connection_mail_templates/${record.id}/edit`}>{I18n.t('generic.edit')}</a>
      </Button>
      <Button key="delete" small={true} handleClick={() => handleClickDelete(record)}>
        {I18n.t('generic.delete')}
      </Button>
    </Buttons>
  )

  return (
    <SidebarLayout user={userData}>
      <Wrapper>
        <Panel
          title={
            <Heading>
              <span>{I18n.t('generic.connection_mail_template')}</span>
              <Button small={true}>
                <a href="/agent/connection_mail_templates/new">{I18n.t('generic.create_new')}</a>
              </Button>
            </Heading>
          }
        >
          <div className="wrapper">
            <div className="record header">
              <div className="record-info">
                <Typography type="subtitle">Target user</Typography>
              </div>
              <div className="record-info">
                <Typography type="subtitle">Blank item</Typography>
              </div>
              <div className="record-info">
                <Typography type="body">{I18n.t('generic.name')}</Typography>
              </div>
              <div className="record-info wide">
                <Typography type="subtitle">Subject</Typography>
              </div>
              <div className="record-info operations" />
            </div>
            {formattedTemplatesList.length === 0 ? (
              <Typography type="subtitle">データがありません</Typography>
            ) : (
              formattedTemplatesList.map((templates, index) => (
                <div key={`template-pair-${index}`} className="parent">
                  {templates.map(template => (
                    <div key={template.id} className="record">
                      <div className="record-info">
                        <StatusBadge
                          type={getBadgeType(template)}
                          label={getTargetUserType(template)}
                        />
                      </div>
                      <div className="record-info">
                        <Typography type="body">
                          {I18n.t(`connection.blank_item.${template.blank_item}`)}
                        </Typography>
                      </div>
                      <div className="record-info">
                        <Typography type="body">{template.title}</Typography>
                      </div>
                      <div className="record-info wide">
                        <Typography type="body">{template.mail_subject}</Typography>
                      </div>

                      <div className="record-info operations">{renderRecordControls(template)}</div>
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
          {pagination && (
            <Pagination
              currentPage={pagination.current_page}
              prevPage={pagination.prev_page}
              nextPage={pagination.next_page}
              totalPages={pagination.total_pages}
              totalCount={pagination.total_count}
              onChangePageHandler={page => {
                location.href = `${location.pathname}?page=${page}`
              }}
            />
          )}
        </Panel>
      </Wrapper>
    </SidebarLayout>
  )
}

const Wrapper = styled.div`
  width: 100%;
  padding: 22px 40px 80px 46px;

  .Panel {
    max-width: none;
  }

  .parent {
    display: flex;
    flex-direction: column;
    padding: 12px 0;
    border-bottom: 1px solid ${COLORS.divider};
  }

  .record {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    &.header {
      font-weight: bold;
    }
    &:not(:first-child) {
      border-top: 1px dotted ${COLORS.divider2};
      padding-top: 12px;
      margin-top: 12px;
    }
  }

  .record-info {
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 0 12px;
    &.wide {
      flex: 3;
    }
    &.operations {
      display: flex;
      gap: 8px;
      width: 140px;
    }
  }
`

const Heading = styled.div`
  .Button {
    margin-left: 12px;
  }
`

export default AgentConnectionMailTemplatesIndex
